import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalsService } from '../../services/globals.service';

/** @deprecated Prefer useBankOffsetJournal + preferredBankAccountCode */
export interface SlimJournalEntryBankGlAccount {
	accountcode: string;
	accountname?: string;
}

export interface SlimJournalEntryDialogData {
	postingDate?: Date | string | null;
	/**
	 * Bank ↔ offset journal: loads bank list via GlobalsService.getBankAccounts (same as Bank Reconciliation cache source)
	 * and GL chart via GlobalsService.getGLAccounts (same as Journal Entry / gl-account-lookup).
	 */
	useBankOffsetJournal?: boolean;
	/** Pre-select bank chart code (e.g. current Bank Reconciliation bank). */
	preferredBankAccountCode?: string | null;
	/** Prefill for bank-offset mode — e.g. posting a bank-feed fee/interest line from the rec. */
	presetAmount?: number | null;
	presetDescription?: string | null;
	presetBankDirection?: 'debit_bank' | 'credit_bank' | null;
	/** @deprecated Use useBankOffsetJournal + preferredBankAccountCode */
	bankGlAccount?: SlimJournalEntryBankGlAccount | null;
}

@Component({
	selector: 'app-slim-journal-entry-dialog',
	templateUrl: './slim-journal-entry-dialog.component.html',
	styleUrls: ['./slim-journal-entry-dialog.component.scss'],
})
export class SlimJournalEntryDialogComponent implements OnInit {
	loading = true;
	posting = false;
	/** Two-line bank + offset flow (e.g. bank rec). */
	bankOffsetMode = false;
	/** Same shape as Bank Reconciliation: globals/bankaccounts (or GlobalSearchService cache). */
	bankAccountsList: any[] = [];
	/** Full chart from globals/glaccounts — same source as Journal Entry. */
	glAccountsAll: any[] = [];
	resolvedBankAccount: any = null;
	tags: any[] = [];
	now = new Date();
	resetjournal = false;
	resetOffsetLookup = false;
	editingIndex: number | null = null;
	preselectedAccount: any = null;
	debits_credits: any[] = [];
	journal_types = ['normal', 'reversing'];
	accountForm!: UntypedFormGroup;

	constructor(
		private fb: UntypedFormBuilder,
		private globalsService: GlobalsService,
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private cdr: ChangeDetectorRef,
		private router: Router,
		private dialogRef: MatDialogRef<SlimJournalEntryDialogComponent, { posted: boolean; transno?: number }>,
		@Inject(MAT_DIALOG_DATA) public data: SlimJournalEntryDialogData | null
	) {}

	ngOnInit(): void {
		const seed = this.data?.postingDate;
		const legacyBankCode = this.data?.bankGlAccount?.accountcode;
		const useBankOffset =
			!!this.data?.useBankOffsetJournal ||
			!!legacyBankCode;
		const preferredBank =
			(this.data?.preferredBankAccountCode != null && String(this.data.preferredBankAccountCode) !== '')
				? String(this.data.preferredBankAccountCode)
				: (legacyBankCode ? String(legacyBankCode) : '');

		forkJoin([
			this.globalsService.getBankAccounts().pipe(catchError(() => of(null))),
			this.globalsService.getGLAccounts().pipe(catchError(() => of([]))),
			this.generalLedgerService.getTags(),
		]).subscribe(([banksRaw, glAccounts, tagsResponse]: [any, any, any]) => {
			this.glAccountsAll = Array.isArray(glAccounts) ? glAccounts : [];
			this.bankAccountsList = this.normalizeBankAccountsList(banksRaw);
			if (!this.bankAccountsList.length) {
				this.globalSearchService.bankaccounts.pipe(take(1)).subscribe((cached: any) => {
					this.bankAccountsList = this.normalizeBankAccountsList(cached);
					this.finishInit(useBankOffset, preferredBank, seed, tagsResponse);
				});
				return;
			}
			this.finishInit(useBankOffset, preferredBank, seed, tagsResponse);
		});
	}

	/** Local calendar date — avoids YYYY-MM-DD parsing as UTC and shifting the displayed day. */
	private parsePostingDate(seed: Date | string | null | undefined): Date {
		const todayLocal = (): Date => {
			const t = new Date();
			return new Date(t.getFullYear(), t.getMonth(), t.getDate());
		};
		if (seed == null || seed === '') {
			return todayLocal();
		}
		if (seed instanceof Date) {
			if (isNaN(seed.getTime())) {
				return todayLocal();
			}
			return new Date(seed.getFullYear(), seed.getMonth(), seed.getDate());
		}
		const s = String(seed).trim();
		const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
		if (ymd) {
			const y = parseInt(ymd[1], 10);
			const mo = parseInt(ymd[2], 10) - 1;
			const day = parseInt(ymd[3], 10);
			const d = new Date(y, mo, day);
			return isNaN(d.getTime()) ? todayLocal() : d;
		}
		const d = new Date(s);
		if (isNaN(d.getTime())) {
			return todayLocal();
		}
		return new Date(d.getFullYear(), d.getMonth(), d.getDate());
	}

	private normalizeBankAccountsList(raw: any): any[] {
		if (raw == null) {
			return [];
		}
		if (Array.isArray(raw)) {
			return raw;
		}
		if (Array.isArray(raw.rows)) {
			return raw.rows;
		}
		return [];
	}

	private finishInit(
		useBankOffset: boolean,
		preferredBank: string,
		seed: Date | string | null | undefined,
		tagsResponse: any
	): void {
		this.tags = (tagsResponse && tagsResponse.tags) ? tagsResponse.tags : [];

		if (useBankOffset) {
			this.bankOffsetMode = true;
			this.setBankOffsetForm(seed, preferredBank);
			// Prefill from the caller (e.g. a bank-feed fee line in the rec).
			const presetAmount = this.data?.presetAmount;
			if (presetAmount != null && Number(presetAmount) > 0) {
				this.accountForm.patchValue({ amount: Math.round(Number(presetAmount) * 100) / 100 });
			}
			if (this.data?.presetDescription) {
				this.accountForm.patchValue({ description: String(this.data.presetDescription).slice(0, 200) });
			}
			if (this.data?.presetBankDirection === 'debit_bank' || this.data?.presetBankDirection === 'credit_bank') {
				this.accountForm.patchValue({ bankDirection: this.data.presetBankDirection });
			}
		} else {
			this.bankOffsetMode = false;
			this.setForm();
			if (seed != null && seed !== '') {
				this.accountForm.patchValue({ trandate: this.parsePostingDate(seed) });
			}
		}
		this.loading = false;
		this.cdr.markForCheck();
	}

	setForm(): void {
		this.accountForm = this.fb.group({
			trandate: [this.now, [Validators.required]],
			type: ['normal', [Validators.required]],
			account: ['', [Validators.required]],
			description: ['', [Validators.required]],
			debitcredit: [0, [Validators.required]],
			entryType: ['debit', [Validators.required]],
			department: [null],
		});
	}

	setBankOffsetForm(seed: Date | string | null | undefined, preferredBankCode: string): void {
		const trandate = this.parsePostingDate(seed);
		const initialCode =
			preferredBankCode &&
			this.bankAccountsList.some((a: any) => String(a.accountcode) === String(preferredBankCode))
				? preferredBankCode
				: (this.bankAccountsList[0]?.accountcode ?? '');
		this.accountForm = this.fb.group({
			bankAccountCode: [initialCode, [Validators.required]],
			trandate: [trandate, [Validators.required]],
			/** Always normal here; reversing journals belong in full Journal entry. */
			type: ['normal', [Validators.required]],
			department: [null],
			bankDirection: ['debit_bank', [Validators.required]],
			amount: [null, [Validators.required, Validators.min(0.01)]],
			description: ['', [Validators.required]],
			offsetAccount: [null, [Validators.required]],
		});
		this.applyBankGlResolution(String(initialCode || ''));
	}

	onBankAccountChange(): void {
		const code = String(this.accountForm?.get('bankAccountCode')?.value || '');
		this.applyBankGlResolution(code);
	}

	/** Merge bank account row (globals/bankaccounts) with chartmaster row (globals/glaccounts). */
	private applyBankGlResolution(accountCode: string): void {
		if (!accountCode) {
			this.resolvedBankAccount = null;
			this.updateDepartmentRequiredForBankOffset();
			this.cdr.markForCheck();
			return;
		}
		const bankRow = this.bankAccountsList.find((a: any) => String(a.accountcode) === String(accountCode));
		const glRow = this.glAccountsAll.find((a: any) => String(a.accountcode) === String(accountCode));
		this.resolvedBankAccount = glRow || {
			accountcode: accountCode,
			accountname: bankRow?.bankaccountname || bankRow?.accountname || accountCode,
			sectioninaccounts: bankRow?.sectioninaccounts ?? glRow?.sectioninaccounts,
		};
		this.updateDepartmentRequiredForBankOffset();
		this.cdr.markForCheck();
	}

	addGl(event: any): void {
		const selection = event?.option?.value ?? event;
		this.accountForm.get('account')?.setValue(selection);
		this.updateDepartmentRequiredState(selection);
	}

	addOffsetGl(event: any): void {
		const selection = event?.option?.value ?? event;
		this.accountForm.get('offsetAccount')?.setValue(selection);
		this.updateDepartmentRequiredForBankOffset();
	}

	private isAccountDepartmentRequired(acc: any): boolean {
		if (!acc) {
			return false;
		}
		const v = acc.department_required;
		return v === 1 || v === '1' || v === true;
	}

	private updateDepartmentRequiredForBankOffset(): void {
		const deptControl = this.accountForm?.get('department');
		if (!deptControl) {
			return;
		}
		const off = this.accountForm.get('offsetAccount')?.value;
		const need = this.isAccountDepartmentRequired(this.resolvedBankAccount) || this.isAccountDepartmentRequired(off);
		if (need) {
			deptControl.setValidators([Validators.required]);
		} else {
			deptControl.clearValidators();
		}
		deptControl.updateValueAndValidity();
		this.cdr.markForCheck();
	}

	private updateDepartmentRequiredState(account: any): void {
		const deptControl = this.accountForm?.get('department');
		if (!deptControl) {
			return;
		}
		if (this.isAccountDepartmentRequired(account)) {
			deptControl.setValidators([Validators.required]);
		} else {
			deptControl.clearValidators();
		}
		deptControl.updateValueAndValidity();
		this.cdr.markForCheck();
	}

	isDepartmentRequired(): boolean {
		if (this.bankOffsetMode) {
			const off = this.accountForm?.get('offsetAccount')?.value;
			return this.isAccountDepartmentRequired(this.resolvedBankAccount) || this.isAccountDepartmentRequired(off);
		}
		const account = this.accountForm?.get('account')?.value;
		return this.isAccountDepartmentRequired(account);
	}

	private getLineTagId(line: any): string {
		const d = line?.department;
		if (d == null) {
			return '';
		}
		if (d && (d.tagId !== undefined || d.tagref !== undefined)) {
			return String(d.tagId ?? d.tagref ?? '');
		}
		return typeof d === 'string' ? d : '';
	}

	private hasAccountRequiringDepartment(lines: any[]): boolean {
		return lines?.some((line: any) => this.isAccountDepartmentRequired(line?.account)) ?? false;
	}

	getDebitTotal(): number {
		if (!this.debits_credits.length) {
			return 0;
		}
		return this.debits_credits.reduce((acc: number, items: any) => {
			if (parseFloat(items.debitcredit) >= 0) {
				return acc + parseFloat(items.debitcredit);
			}
			return acc;
		}, 0);
	}

	getCreditTotal(): number {
		if (!this.debits_credits.length) {
			return 0;
		}
		return this.debits_credits.reduce((acc: number, items: any) => {
			if (parseFloat(items.debitcredit) < 0) {
				return acc + Math.abs(parseFloat(items.debitcredit));
			}
			return acc;
		}, 0);
	}

	requiredToBalance(): number {
		if (!this.debits_credits.length) {
			return 0;
		}
		return this.debits_credits.reduce((acc: number, items: any) => acc + parseFloat(items.debitcredit), 0);
	}

	isBalanced(): boolean {
		return Math.abs(this.requiredToBalance()) < 0.005;
	}

	addValue(): void {
		if (!this.accountForm.valid) {
			this.accountForm.markAllAsTouched();
			return;
		}
		const amount = parseFloat(this.accountForm.get('debitcredit')?.value);
		if (!amount && this.editingIndex === null) {
			return;
		}
		const entryType = this.accountForm.get('entryType')?.value;
		const entryData = { ...this.accountForm.value };
		entryData.debitcredit = entryType === 'debit' ? amount : -amount;

		if (this.editingIndex !== null) {
			this.debits_credits[this.editingIndex] = entryData;
			this.cancelEdit();
		} else {
			this.debits_credits.push(entryData);
			this.accountForm.patchValue({
				account: '',
				description: '',
				debitcredit: 0,
			});
			this.updateDepartmentRequiredState(null);
			this.resetjournal = true;
			setTimeout(() => {
				this.resetjournal = false;
			}, 100);
		}
		this.cdr.markForCheck();
	}

	editLine(line: any, index: number): void {
		this.editingIndex = index;
		this.preselectedAccount = line.account;
		const td = line.trandate;
		const trandateVal = td instanceof Date ? td : (td ? new Date(td) : this.accountForm.get('trandate')?.value);
		this.accountForm.patchValue({
			trandate: trandateVal && !isNaN(new Date(trandateVal).getTime()) ? new Date(trandateVal) : this.accountForm.get('trandate')?.value,
			type: line.type || this.accountForm.get('type')?.value,
			account: line.account,
			description: line.description,
			entryType: line.debitcredit >= 0 ? 'debit' : 'credit',
			debitcredit: Math.abs(parseFloat(line.debitcredit)),
			department: line.department ?? null,
		});
		this.updateDepartmentRequiredState(line.account);
	}

	cancelEdit(): void {
		this.editingIndex = null;
		this.preselectedAccount = null;
		this.accountForm.patchValue({
			account: '',
			description: '',
			debitcredit: 0,
		});
		this.updateDepartmentRequiredState(null);
		this.resetjournal = true;
		setTimeout(() => {
			this.resetjournal = false;
		}, 100);
		this.cdr.markForCheck();
	}

	removeDC(row: any): void {
		const index = this.debits_credits.indexOf(row);
		if (index >= 0) {
			this.debits_credits.splice(index, 1);
		}
		this.cdr.markForCheck();
	}

	clearOffsetAccount(): void {
		this.accountForm.patchValue({ offsetAccount: null });
		this.updateDepartmentRequiredForBankOffset();
		this.resetOffsetLookup = true;
		setTimeout(() => {
			this.resetOffsetLookup = false;
		}, 50);
		this.cdr.markForCheck();
	}

	getOffsetAccount(): any {
		return this.accountForm?.get('offsetAccount')?.value;
	}

	bankOffsetPreview(): { bankAmt: number; offsetAmt: number } | null {
		if (!this.bankOffsetMode || !this.resolvedBankAccount) {
			return null;
		}
		const raw = this.accountForm?.get('amount')?.value;
		const amt = Math.abs(parseFloat(raw));
		if (!Number.isFinite(amt) || amt <= 0) {
			return null;
		}
		const debitBank = this.accountForm.get('bankDirection')?.value === 'debit_bank';
		const bankAmt = debitBank ? amt : -amt;
		return { bankAmt, offsetAmt: -bankAmt };
	}

	private handleJournalPostResult(result: any, onSuccess?: () => void): void {
		const color = result?.success ? 'success' : 'danger';
		this.globalSearchService.showNotification(
			result?.message || (result?.success ? 'Journal posted' : 'Post failed'),
			color,
			'bottom',
			'center'
		);
		if (result?.success && result?.date_adjusted) {
			const req = result.requested_date || '';
			const eff = result.effective_date || '';
			const detail = req && eff ? ` Requested: ${req} → Posted: ${eff}.` : ' Posting date was moved to today (period was closed).';
			this.globalSearchService.showNotification('Period was closed.' + detail, 'warning', 'bottom', 'center');
		}
		if (result?.success && onSuccess) {
			onSuccess();
		}
	}

	private submitJournalLines(lines: any[]): void {
		const dept = this.accountForm.get('department')?.value;
		const glTag = (dept && dept.tagId !== undefined) ? dept.tagId : (dept || '');
		if (this.hasAccountRequiringDepartment(lines)) {
			const glTagStr = glTag !== '' && glTag != null ? String(glTag) : '';
			for (const t of lines) {
				if (!this.isAccountDepartmentRequired(t?.account)) {
					continue;
				}
				const effective = this.getLineTagId(t) || glTagStr;
				if (!effective) {
					this.globalSearchService.showNotification(
						'Department is required for this GL account.',
						'danger',
						'bottom',
						'center'
					);
					return;
				}
			}
		}
		const transactions = lines.map((t: any) => ({
			...t,
			tag: this.getLineTagId(t) || glTag || undefined,
		}));
		const payload = { transactions, gl_tag: glTag || '' };
		this.posting = true;
		this.generalLedgerService.postJournalEntry(payload).subscribe({
			next: (result: any) => {
				this.posting = false;
				this.handleJournalPostResult(result, () => {
					this.dialogRef.close({ posted: true, transno: result?.transno });
				});
				this.cdr.markForCheck();
			},
			error: () => {
				this.posting = false;
				this.globalSearchService.showNotification('Journal post failed.', 'danger', 'bottom', 'center');
				this.cdr.markForCheck();
			},
		});
	}

	postBankOffsetJournal(): void {
		this.updateDepartmentRequiredForBankOffset();
		if (!this.accountForm.valid) {
			this.accountForm.markAllAsTouched();
			return;
		}
		const v = this.accountForm.getRawValue();
		const amt = Math.abs(parseFloat(v.amount));
		if (!Number.isFinite(amt) || amt < 0.01 || !this.resolvedBankAccount || !v.offsetAccount) {
			return;
		}
		const debitBank = v.bankDirection === 'debit_bank';
		const bankAmt = debitBank ? amt : -amt;
		const offAmt = -bankAmt;
		const lineCommon = {
			trandate: v.trandate,
			type: v.type,
			description: v.description,
			department: v.department,
		};
		const lines = [
			{ ...lineCommon, account: this.resolvedBankAccount, debitcredit: bankAmt },
			{ ...lineCommon, account: v.offsetAccount, debitcredit: offAmt },
		];
		this.submitJournalLines(lines);
	}

	postJournal(): void {
		if (this.bankOffsetMode) {
			this.postBankOffsetJournal();
			return;
		}
		if (!this.debits_credits.length || !this.isBalanced()) {
			return;
		}
		this.submitJournalLines(this.debits_credits);
	}

	openFullJournal(): void {
		this.dialogRef.close({ posted: false });
		this.router.navigate(['/general-ledger/journal-entry']);
	}

	close(): void {
		this.dialogRef.close({ posted: false });
	}
}
